import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, ChevronLeft, MapPin, Play, X } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CONTACT_URL = 'https://www.weddingvideo.co.uk/contact';

const VenueDetail = () => {
  const { slug } = useParams();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setNotFound(false);

    // Fire visit tracking (utm_source drives the 'source' field)
    try {
      const params = new URLSearchParams(window.location.search);
      const source = params.get('utm_source') || 'direct';
      fetch(`${BACKEND_URL}/api/venues/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, source }),
      }).catch(() => {});
    } catch {}

    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/venues/${slug}`);
        if (r.status === 404) {
          if (!ignore) { setNotFound(true); setLoading(false); }
          return;
        }
        const d = await r.json();
        if (!ignore) {
          setVenue(d);
          setLoading(false);
          document.title = `Wedding Videographer & Photographer at ${d.name} | Wedding Video Essex`;
          let m = document.querySelector('meta[name="description"]');
          if (!m) {
            m = document.createElement('meta');
            m.setAttribute('name', 'description');
            document.head.appendChild(m);
          }
          m.setAttribute(
            'content',
            `Real cinematic wedding films and photography from ${d.name}${d.location ? ` in ${d.location}` : ''}. Explore our galleries and see why couples choose Wedding Video Essex.`,
          );
        }
      } catch {
        if (!ignore) { setNotFound(true); setLoading(false); }
      }
    })();

    return () => { ignore = true; };
  }, [slug]);

  // Lightbox keyboard nav
  useEffect(() => {
    if (!lightbox.open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox({ open: false, index: 0 });
      if (e.key === 'ArrowRight') setLightbox((s) => ({ ...s, index: (s.index + 1) % venue.photos.length }));
      if (e.key === 'ArrowLeft') setLightbox((s) => ({ ...s, index: (s.index - 1 + venue.photos.length) % venue.photos.length }));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox.open, venue]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#1A1A1A' }}>
        <p className="text-white/50">Loading venue…</p>
      </div>
    );
  }

  if (notFound || !venue) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#1A1A1A' }}>
        <div className="text-center text-white">
          <h1 className="font-serif text-3xl mb-4">Venue not found</h1>
          <Link to="/venues" className="text-gold underline">Browse all venues</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`venue-${venue.slug}`} style={{ backgroundColor: '#1A1A1A' }}>
      <Navigation />

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'LocalBusiness',
            name: `Wedding Video Essex — ${venue.name}`,
            description: venue.description,
            url: `https://book.weddingvideo.co.uk/venues/${venue.slug}`,
            image: venue.hero_image,
            areaServed: venue.location || 'Essex',
            priceRange: '£££',
          }),
        }}
      />

      {/* HERO */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        {venue.hero_image && (
          <div className="absolute inset-0">
            <img src={venue.hero_image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-[#1A1A1A]" />
          </div>
        )}
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl text-center pt-16">
          <Link to="/venues" data-testid="back-to-venues" className="inline-flex items-center gap-1 text-white/50 hover:text-gold text-xs uppercase tracking-widest mb-6 transition-colors">
            <ChevronLeft className="w-3 h-3" /> All venues
          </Link>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold/10 border border-gold/30 mb-5">
            <MapPin className="w-4 h-4 text-gold" />
            <span className="text-gold text-xs font-semibold uppercase tracking-widest">
              {venue.location || 'Wedding Venue'}
            </span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight">
            {venue.name}
          </h1>
          {venue.description && (
            <p className="text-white/75 text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
              {venue.description}
            </p>
          )}
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={CONTACT_URL}
              data-testid={`${venue.slug}-cta-primary`}
              className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-black font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:scale-[1.02]"
            >
              Secure My {venue.name} Date <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* VIDEOS */}
      {venue.videos.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-8 py-20 border-t border-white/5">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <p className="text-gold text-xs font-semibold uppercase tracking-widest mb-3">Real weddings</p>
              <h2 className="font-serif text-3xl sm:text-4xl text-white mb-3">Films from {venue.name}</h2>
              <p className="text-white/50 text-sm">Cinematic wedding films shot at this venue.</p>
            </div>
            <div className={`grid gap-5 ${venue.videos.length === 1 ? 'grid-cols-1 max-w-3xl mx-auto' : venue.videos.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
              {venue.videos.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  data-testid={`venue-video-${v.id}`}
                  onClick={() => setPlayingVideo(v.id)}
                  className="group relative aspect-video overflow-hidden rounded-xl bg-black cursor-pointer"
                >
                  <img
                    src={`https://img.youtube.com/vi/${v.id}/maxresdefault.jpg`}
                    alt={v.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="w-7 h-7 text-black ml-0.5" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent text-left">
                    <p className="text-white text-sm font-medium">{v.title}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PHOTOS */}
      {venue.photos.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-8 py-20 border-t border-white/5">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <p className="text-gold text-xs font-semibold uppercase tracking-widest mb-3">Gallery</p>
              <h2 className="font-serif text-3xl sm:text-4xl text-white mb-3">Photography from {venue.name}</h2>
              <p className="text-white/50 text-sm">Click any image to view full-size.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {venue.photos.map((url, i) => (
                <button
                  key={url}
                  type="button"
                  data-testid={`venue-photo-${i}`}
                  onClick={() => setLightbox({ open: true, index: i })}
                  className="aspect-[3/4] overflow-hidden rounded-lg bg-black cursor-pointer group"
                >
                  <img
                    src={url}
                    alt={`${venue.name} wedding photo ${i + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="px-4 sm:px-6 lg:px-8 py-24 border-t border-white/5">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="font-serif text-3xl sm:text-5xl text-white mb-6 leading-tight">
            Getting married at {venue.name}?
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-2xl mx-auto">
            We'd love to be part of your day. Cinematic film, luxury photography — beautifully told.
          </p>
          <a
            href={CONTACT_URL}
            data-testid={`${venue.slug}-cta-final`}
            className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black font-semibold px-10 py-5 rounded-full text-base transition-all duration-300 hover:scale-[1.02]"
          >
            Secure My {venue.name} Date <ChevronRight className="w-5 h-5" />
          </a>
          <p className="text-white/30 text-xs mt-6 uppercase tracking-widest">Zero pressure · Zero obligation</p>
        </div>
      </section>

      <Footer />

      {/* Photo Lightbox */}
      {lightbox.open && venue.photos.length > 0 && (
        <div
          role="dialog"
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightbox({ open: false, index: 0 })}
        >
          <button
            type="button"
            className="absolute top-5 right-5 text-white/70 hover:text-white p-2"
            onClick={(e) => { e.stopPropagation(); setLightbox({ open: false, index: 0 }); }}
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <button
            type="button"
            className="absolute left-3 sm:left-6 text-white/70 hover:text-white p-3"
            onClick={(e) => { e.stopPropagation(); setLightbox((s) => ({ ...s, index: (s.index - 1 + venue.photos.length) % venue.photos.length })); }}
            aria-label="Previous"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <img
            src={venue.photos[lightbox.index]}
            alt={`${venue.name} ${lightbox.index + 1}`}
            className="max-h-[90vh] max-w-[92vw] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            className="absolute right-3 sm:right-6 text-white/70 hover:text-white p-3"
            onClick={(e) => { e.stopPropagation(); setLightbox((s) => ({ ...s, index: (s.index + 1) % venue.photos.length })); }}
            aria-label="Next"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
          <p className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/50 text-xs uppercase tracking-widest">
            {lightbox.index + 1} / {venue.photos.length}
          </p>
        </div>
      )}

      {/* Video Modal */}
      {playingVideo && (
        <div
          role="dialog"
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setPlayingVideo(null)}
        >
          <button
            type="button"
            className="absolute top-5 right-5 text-white/70 hover:text-white p-2"
            onClick={() => setPlayingVideo(null)}
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="w-full max-w-5xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${playingVideo}?autoplay=1&rel=0&modestbranding=1`}
              title="Wedding film"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default VenueDetail;
